'use client';

import { CSSProperties, RefObject, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import useNavigationMenuTracking from 'components/analytics/hooks/useNavigationMenuTracking';
import { trackNavigationBarInteraction } from 'components/analytics/eventHandlerTracking';
import { useBreakpoint } from 'hooks/useBreakpoints';
import { usePageOverlayLock } from 'hooks/usePageOverlayLock';
import { BackgroundColor } from 'types/constants';
import { cn } from 'utils/styles';
import { BACKGROUND_FADE_DURATION } from './animations';
import HorizontalNavBar from './HorizontalNavBar';
import NavContentMenu from './NavContentMenu';
import { MainNavProps, NavContentLinkProps, NavSubLinkProps } from './types';

interface HorizontalNavProps extends MainNavProps {
  menuIsOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  contentMenuOffsetHeight: number;
  navRef: RefObject<HTMLElement | null>;
}

export const HorizontalNav = ({
  cta,
  resourcesLabel,
  resourcesCollection,
  resourcesUrl,
  resourcesInternalPage,
  logoHref: logoHrefProp,
  navContent,
  topBarLink,
  className,
  menuIsOpen,
  openMenu,
  closeMenu,
  contentMenuOffsetHeight,
  navRef,
}: HorizontalNavProps) => {
  const [subMenuIsOpen, setSubMenuIsOpen] = useState(false);
  const [nestedSubMenuIsOpen, setNestedSubMenuIsOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState<NavContentLinkProps | null>(null);
  const [activeNestedSubLink, setActiveNestedSubLink] = useState<NavSubLinkProps | null>(null);
  const [menuIsScrolled, setMenuIsScrolled] = useState(false);
  const { track: navigationTrack } = useNavigationMenuTracking();
  const { isAboveMd } = useBreakpoint('md');
  const logoHref = logoHrefProp ?? '/';
  const showShadows = menuIsScrolled && menuIsOpen;
  const navStyle: CSSProperties & { '--menu-offset-height': string } = {
    '--menu-offset-height': `${contentMenuOffsetHeight}px`,
  };
  // With the mobile menu open the submenu renders inside it rather than veiling the page.
  const veiledSubMenu = menuIsOpen ? null : activeSubMenu;
  const overlayIsOpen = menuIsOpen || veiledSubMenu != null;

  // The veil is `md:block`, so below md the page is uncovered and must stay reachable.
  usePageOverlayLock({ inert: menuIsOpen || (veiledSubMenu != null && isAboveMd), lockScroll: menuIsOpen });

  const resetSubMenus = () => {
    setSubMenuIsOpen(false);
    setNestedSubMenuIsOpen(false);
    setActiveSubMenu(null);
    setActiveNestedSubLink(null);
  };

  const closeHorizontalMenu = () => {
    resetSubMenus();
    setMenuIsScrolled(false);
    closeMenu();
  };

  const closeSubMenu = () => {
    setSubMenuIsOpen(false);
    setNestedSubMenuIsOpen(false);
    setActiveNestedSubLink(null);

    navigationTrack({
      level: 1,
      action_detail: 'Sidebar Submenu Closed',
    });

    trackNavigationBarInteraction({
      button_label: activeSubMenu?.link?.label ?? 'Sidebar Submenu',
      action: 'close',
      level: '1',
    });
  };

  const openSubMenu = (subMenu: NavContentLinkProps) => {
    setActiveSubMenu(subMenu);
    setNestedSubMenuIsOpen(false);
    setActiveNestedSubLink(null);
    setSubMenuIsOpen(true);

    navigationTrack({
      location: subMenu.link.label,
      level: 1,
      action_detail: 'Sidebar Submenu Opened',
    });

    trackNavigationBarInteraction({
      button_label: subMenu.link.label,
      action: 'open',
      level: '1',
    });
  };

  const closeNestedSubMenu = () => {
    setNestedSubMenuIsOpen(false);
    setActiveNestedSubLink(null);
    setSubMenuIsOpen(true);

    navigationTrack({
      level: 2,
      action_detail: 'Sidebar Nested Submenu Closed',
    });

    trackNavigationBarInteraction({
      button_label: activeNestedSubLink?.link?.label ?? 'Sidebar Nested Submenu',
      action: 'close',
      level: '2',
    });
  };

  const openNestedSubMenu = (subLink: NavSubLinkProps) => {
    setActiveNestedSubLink(subLink);
    setNestedSubMenuIsOpen(true);

    navigationTrack({
      location: subLink.link.label,
      level: 2,
      action_detail: 'Sidebar Nested Submenu Opened',
    });

    trackNavigationBarInteraction({
      button_label: subLink.link.label,
      action: 'open',
      level: '2',
    });
  };

  const closeHorizontalSubMenu = (subMenu: NavContentLinkProps) => {
    setActiveSubMenu(null);

    navigationTrack({
      level: 1,
      action_detail: 'Horizontal Submenu Closed',
    });

    trackNavigationBarInteraction({
      button_label: subMenu.link.label,
      action: 'close',
      level: '1',
    });
  };

  const toggleHorizontalSubmenu = (subMenu: NavContentLinkProps) => {
    if (subMenu === activeSubMenu) {
      closeHorizontalSubMenu(subMenu);
      return;
    }

    setActiveSubMenu(subMenu);
    navigationTrack({
      location: subMenu.link.label,
      level: 1,
      action_detail: 'Horizontal Submenu Opened',
    });

    trackNavigationBarInteraction({
      button_label: subMenu.link.label,
      action: 'open',
      level: '1',
    });
  };

  const handleMenuScroll = (event: React.UIEvent<HTMLDivElement>) => {
    setMenuIsScrolled(event.currentTarget.scrollTop > 0);
  };

  const dismissOverlay = () => {
    if (menuIsOpen) {
      closeHorizontalMenu();
      return;
    }

    if (veiledSubMenu != null) {
      closeHorizontalSubMenu(veiledSubMenu);
    }
  };

  const dismissOverlayRef = useRef(dismissOverlay);
  useEffect(() => {
    dismissOverlayRef.current = dismissOverlay;
  });

  useEffect(() => {
    if (!overlayIsOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismissOverlayRef.current();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [overlayIsOpen]);

  return (
    <nav
      className={cn('sticky top-0 left-0 right-0', BackgroundColor.White, className)}
      aria-label="Main navigation"
      data-testid="navHorizontal"
      style={navStyle}
      ref={navRef}>
      <HorizontalNavBar
        topBarLink={topBarLink}
        topBarCta={cta}
        showShadows={showShadows}
        navContent={navContent}
        logoHref={logoHref}
        resourcesLabel={resourcesLabel}
        resourcesCollection={resourcesCollection}
        resourcesUrl={resourcesUrl}
        resourcesInternalPage={resourcesInternalPage}
        openMenu={openMenu}
        closeMenu={closeHorizontalMenu}
        menuIsOpen={menuIsOpen}
        closeSubMenu={closeSubMenu}
        closeNestedSubMenu={closeNestedSubMenu}
        subMenuIsOpen={subMenuIsOpen}
        nestedSubMenuIsOpen={nestedSubMenuIsOpen}
        activeSubMenu={activeSubMenu}
        activeNestedSubLinkLabel={activeNestedSubLink?.link.label}
        handleSubMenuClick={toggleHorizontalSubmenu}
      />

      <AnimatePresence>
        {veiledSubMenu && (
          <m.button
            type="button"
            aria-label="Close submenu"
            className="fixed inset-x-0 bottom-0 top-(--menu-offset-height) z-40 hidden bg-scrim/60 md:block"
            animate={{ opacity: [0, 1], transition: { duration: BACKGROUND_FADE_DURATION } }}
            exit={{ opacity: [1, 0], transition: { duration: BACKGROUND_FADE_DURATION } }}
            onClick={() => closeHorizontalSubMenu(veiledSubMenu)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {menuIsOpen && (
          <NavContentMenu
            subMenuIsOpen={subMenuIsOpen}
            nestedSubMenuIsOpen={nestedSubMenuIsOpen}
            showShadows={showShadows}
            cta={cta}
            activeSubMenu={activeSubMenu}
            activeNestedSubLink={activeNestedSubLink}
            resourcesCollection={resourcesCollection}
            resourcesLabel={resourcesLabel}
            resourcesInternalPage={resourcesInternalPage}
            resourcesUrl={resourcesUrl}
            navContent={navContent}
            handleMenuScroll={handleMenuScroll}
            handleSubMenuClick={openSubMenu}
            handleNestedSubLinkClick={openNestedSubMenu}
            closeMenu={closeHorizontalMenu}
            enableNestedDrillIn
          />
        )}
      </AnimatePresence>
    </nav>
  );
};
